import {alertClasses, Breakpoints, ThemeOptions} from '@mui/material';

import {Tokens} from '../../../../types/generated/Tokens';

export const MuiAlert = ({alert}: Tokens, mediaQueries: Breakpoints): ThemeOptions['components'] => {
  const {base, error, warning, info, neutral, success, content, title} = alert;

  const baseBorderRadius = base.desktop.container.border.radius.all;
  const baseBorderStyle = `${base.desktop.container.border.width} ${base.desktop.container.border.style}`;

  const stylesError = {
    backgroundColor: error.desktop.container.background.color,
    border: baseBorderStyle,
    borderColor: error.desktop.container.border.color,

    [`& .${alertClasses.icon}`]: {
      color: error.desktop.icon.color
    },

    // For direct MUI Alert content (plain text / non-Typography nodes)
    [`& .${alertClasses.message}`]: {
      color: error.desktop.content.font.color
    },

    [`& .${alertClasses.message} .MuiTypography-root:not(.MuiLink-root):not(.MuiAlertTitle-root)`]: {
      color: error.desktop.content.font.color
    },

    [`& .${alertClasses.message} > .MuiAlertTitle-root`]: {
      color: error.desktop.title.font.color
    },

    '& .MuiAlert-customClose, & .MuiAlert-customClose:hover': {
      color: error.desktop.closeBtn.color
    }
  };

  const stylesInfo = {
    backgroundColor: info.desktop.container.background.color,
    border: baseBorderStyle,
    borderColor: info.desktop.container.border.color,

    [`& .${alertClasses.icon}`]: {
      color: info.desktop.icon.color
    },

    // For direct MUI Alert content (plain text / non-Typography nodes)
    [`& .${alertClasses.message}`]: {
      color: info.desktop.content.font.color
    },

    [`& .${alertClasses.message} .MuiTypography-root:not(.MuiLink-root):not(.MuiAlertTitle-root)`]: {
      color: info.desktop.content.font.color
    },

    [`& .${alertClasses.message} > .MuiAlertTitle-root`]: {
      color: info.desktop.title.font.color
    },

    '& .MuiAlert-customClose, & .MuiAlert-customClose:hover': {
      color: info.desktop.closeBtn.color
    }
  };

  const stylesNeutral = {
    backgroundColor: neutral.desktop.container.background.color,
    border: baseBorderStyle,
    borderColor: neutral.desktop.container.border.color,

    [`& .${alertClasses.icon}`]: {
      color: neutral.desktop.icon.color
    },

    // For direct MUI Alert content (plain text / non-Typography nodes)
    [`& .${alertClasses.message}`]: {
      color: neutral.desktop.content.font.color
    },

    [`& .${alertClasses.message} .MuiTypography-root:not(.MuiLink-root):not(.MuiAlertTitle-root)`]: {
      color: neutral.desktop.content.font.color
    },

    [`& .${alertClasses.message} > .MuiAlertTitle-root`]: {
      color: neutral.desktop.title.font.color
    },

    '& .MuiAlert-customClose, & .MuiAlert-customClose:hover': {
      color: neutral.desktop.closeBtn.color
    }
  };

  const stylesSuccess = {
    backgroundColor: success.desktop.container.background.color,
    border: baseBorderStyle,
    borderColor: success.desktop.container.border.color,

    [`& .${alertClasses.icon}`]: {
      color: success.desktop.icon.color
    },

    // For direct MUI Alert content (plain text / non-Typography nodes)
    [`& .${alertClasses.message}`]: {
      color: success.desktop.content.font.color
    },

    [`& .${alertClasses.message} .MuiTypography-root:not(.MuiLink-root):not(.MuiAlertTitle-root)`]: {
      color: success.desktop.content.font.color
    },

    [`& .${alertClasses.message} > .MuiAlertTitle-root`]: {
      color: success.desktop.title.font.color
    },

    '& .MuiAlert-customClose, & .MuiAlert-customClose:hover': {
      color: success.desktop.closeBtn.color
    }
  };

  const stylesWarning = {
    backgroundColor: warning.desktop.container.background.color,
    border: baseBorderStyle,
    borderColor: warning.desktop.container.border.color,

    [`& .${alertClasses.icon}`]: {
      color: warning.desktop.icon.color
    },

    // For direct MUI Alert content (plain text / non-Typography nodes)
    [`& .${alertClasses.message}`]: {
      color: warning.desktop.content.font.color
    },

    [`& .${alertClasses.message} .MuiTypography-root:not(.MuiLink-root):not(.MuiAlertTitle-root)`]: {
      color: warning.desktop.content.font.color
    },

    [`& .${alertClasses.message} > .MuiAlertTitle-root`]: {
      color: warning.desktop.title.font.color
    },

    '& .MuiAlert-customClose, & .MuiAlert-customClose:hover': {
      color: warning.desktop.closeBtn.color
    }
  };

  return {
    MuiAlert: {
      styleOverrides: {
        /* Styles applied to the root element. */
        root: {
          borderRadius: baseBorderRadius,
          border: baseBorderStyle,
          padding: base.desktop.container.padding.all,
          flexWrap: 'wrap',
          position: 'relative',
          width: '100%',

          '&.MuiAlert-colorNeutral.MuiAlert-standard, &.MuiAlert-colorNeutral.MuiAlert-outlined, &.MuiAlert-colorNeutral.MuiAlert-filled':
            stylesNeutral,

          // on desktop, fit everything in one single row
          [mediaQueries.up('sm')]: {
            alignItems: 'flex-start',
            flexWrap: 'nowrap'
          }
        },

        /* Styles applied to the root element if `variant="standard"` and `color="success"`. */
        standardSuccess: {
          ...stylesSuccess
        },

        /* Styles applied to the root element if `variant="standard"` and `color="info"`. */
        standardInfo: {
          ...stylesInfo
        },

        /* Styles applied to the root element if `variant="standard"` and `color="warning"`. */
        standardWarning: {
          ...stylesWarning
        },

        /* Styles applied to the root element if `variant="standard"` and `color="error"`. */
        standardError: {
          ...stylesError
        },

        /* Styles applied to the root element if `variant="outlined"` and `color="success"`. */
        outlinedSuccess: {
          ...stylesSuccess
        },

        /* Styles applied to the root element if `variant="outlined"` and `color="info"`. */
        outlinedInfo: {
          ...stylesInfo
        },

        /* Styles applied to the root element if `variant="outlined"` and `color="warning"`. */
        outlinedWarning: {
          ...stylesWarning
        },

        /* Styles applied to the root element if `variant="outlined"` and `color="error"`. */
        outlinedError: {
          ...stylesError
        },

        /* Styles applied to the root element if `variant="filled"` and `color="success"`. */
        filledSuccess: {
          ...stylesSuccess
        },

        /* Styles applied to the root element if `variant="filled"` and `color="info"`. */
        filledInfo: {
          ...stylesInfo
        },

        /* Styles applied to the root element if `variant="filled"` and `color="warning"`. */
        filledWarning: {
          ...stylesWarning
        },

        /* Styles applied to the root element if `variant="filled"` and `color="error"`. */
        filledError: {
          ...stylesError
        },

        /* Styles applied to the icon wrapper element. */
        icon: {
          marginRight: 8,
          fontSize: 32,
          padding: 0,
          opacity: 1
        },

        /* Styles applied to the message wrapper element. */
        message: {
          padding: 0,
          flexGrow: 1,
          margin: 0,
          fontSize: content.desktop.font.size,
          lineHeight: content.desktop.font.lineHeight,
          maxWidth: '100%',
          overflow: 'unset',

          [mediaQueries.down('sm')]: {
            maxWidth: `calc(100% - 64px)`, // to accommodate the left and right side icon
            fontSize: content.mobile.font.size,
            lineHeight: content.mobile.font.lineHeight
          },

          // targets the title
          '& > .MuiAlertTitle-root': {
            marginBottom: 0,
            fontSize: title.desktop.font.size,
            fontWeight: '700',
            lineHeight: '26px', // to accommodate the 4px bottom gutter
            [mediaQueries.down('sm')]: {
              marginTop: 2,
              fontSize: title.mobile.font.size,
              lineHeight: title.mobile.font.lineHeight
            }
          }
        },

        /* Styles applied to the action wrapper element if `action` is provided. */
        action: {
          alignSelf: 'stretch',
          flexBasis: '100%',
          flexShrink: 0,
          flexWrap: 'wrap',
          margin: 0,
          padding: 0,

          [mediaQueries.up('sm')]: {
            flexBasis: 'auto'
          },

          // general custom actions
          '& > .MuiAlert-customAction': {
            marginTop: 16,
            width: '100%',

            [mediaQueries.up('sm')]: {
              alignSelf: 'center',
              margin: 0,
              marginLeft: 8,
              marginRight: 8,
              minWidth: 'auto',
              width: 'auto'
            }
          },
          // close button
          '& > *:not(.MuiAlert-customAction), & > *:not(.MuiAlert-customAction):hover': {
            marginTop: '-4px',
            marginRight: '-4px',
            alignSelf: 'flex-start',
            [mediaQueries.down('sm')]: {
              position: 'absolute',
              top: 12,
              right: 12
            }
          }
        }
      }
    }
  };
};
